/**
 * useRefetchJsonStructure — actively re-samples a column from the database
 * and returns the freshly inferred structure as a *preview* (does NOT
 * persist). The caller is responsible for explicitly applying the outcome
 * via `upsertJsonStructureCache` once the user confirms — see
 * `JsonStructureInspector` for the dirty-state Apply / Discard flow.
 *
 * The renderer's passive aggregator (`jsonStructureAggregatorStore`) only
 * sees rows the user has already loaded into a grid; this hook lets the user
 * explicitly pull a larger / filtered sample so the inferred structure
 * matches what they actually care about.
 */

import { useCallback, useState } from 'react';
import type { JsonStructureCachePath } from '@/types/electron';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type {
  FilterGroup,
  TableExplorerColumn,
} from '../../core/types/workspaceTypes';
import { fetchTableExplorerRows } from '../../table-explorer/services/tableExplorerApi';
import { inferJsonStructureFromValues } from '../utils/inferJsonStructure';

export const REFRESH_SAMPLE_LIMIT = 500;

/** Stable execution-id for one refetch invocation. The renderer only uses
 * this to thread cancellation through; nothing here calls cancelQueryV2. */
const newExecutionId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? `json-refetch-${crypto.randomUUID()}`
    : `json-refetch-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export type UseRefetchJsonStructureArgs = {
  connectionId: string | null | undefined;
  database: string | null | undefined;
  schema: string;
  table: string;
  column: string;
  engine: DatabaseEngineId | undefined;
};

export type RefetchOutcome = {
  paths: JsonStructureCachePath[];
  sampledRows: number;
  jsonRows: number;
  sampledAt: string;
  /** Columns discovered on the table — handy for hydrating a FilterWizard. */
  columns: TableExplorerColumn[];
};

export function useRefetchJsonStructure(args: UseRefetchJsonStructureArgs) {
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastOutcome, setLastOutcome] = useState<RefetchOutcome | null>(null);

  // `args.schema` is intentionally NOT presence-checked: it's a required
  // string that is `''` on schemaless engines (Kusto/ADX) — a valid schema,
  // not a missing one. Truthy-checking it would permanently disable the JSON
  // structure "Refresh" for every Kusto dynamic column.
  const canRefetch = Boolean(
    args.connectionId && args.database && args.engine && args.table && args.column,
  );

  const refetch = useCallback(
    async (filters: FilterGroup[] = []): Promise<RefetchOutcome | null> => {
      if (!args.connectionId || !args.database || !args.engine) {
        setError('Missing connection context');
        return null;
      }
      setIsRefetching(true);
      setError(null);
      try {
        // Sample the JSON column for inference. `columns: '*'` keeps the
        // returned `columns` metadata complete so the inspector's FilterWizard
        // sees every table column, not just the one we sampled.
        const result = await fetchTableExplorerRows({
          connectionId: args.connectionId,
          executionId: newExecutionId(),
          table: {
            database: args.database,
            schema: args.schema,
            tableName: args.table,
          },
          columns: '*',
          limit: REFRESH_SAMPLE_LIMIT,
          filters,
          sorting: [],
        });
        if (!result.success) {
          throw new Error(result.error || 'Refetch failed');
        }
        const rows = result.rows ?? [];
        // The column may come back under any case (engines normalize
        // identifiers). Find the matching key in the first row, else fall
        // through to the literal column name.
        let columnKey = args.column;
        if (rows.length > 0) {
          const firstRow = rows[0];
          const keys = Object.keys(firstRow);
          const matched = keys.find((k) => k.toLowerCase() === args.column.toLowerCase());
          if (matched) columnKey = matched;
        }
        const values = rows.map((row) => row[columnKey]);
        const inferred = inferJsonStructureFromValues(values);

        const outcome: RefetchOutcome = {
          paths: inferred.paths,
          sampledRows: inferred.sampledRows,
          jsonRows: inferred.jsonRows,
          sampledAt: new Date().toISOString(),
          columns: result.columns ?? [],
        };
        setLastOutcome(outcome);
        return outcome;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Refetch failed';
        setError(message);
        return null;
      } finally {
        setIsRefetching(false);
      }
    },
    [args.connectionId, args.database, args.engine, args.schema, args.table, args.column],
  );

  return { refetch, isRefetching, error, lastOutcome, canRefetch };
}
